import { httpsCallable } from 'firebase/functions';
import { useState, useEffect } from 'react';
import { fbFunctions } from '../../../firebase/firebase';
import Button from '../buttons/Buttons';
function MaintenanceCountdown() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState('');
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isButtonActive, setIsButtonActive] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [hoveredLinks, setHoveredLinks] = useState({});
  const [hoveredSocial, setHoveredSocial] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine grid layout based on screen size
  const getGridTemplateColumns = () => {
    if (windowWidth < 640) {
      return '1fr'; // Single column on mobile
    } else if (windowWidth < 1024) {
      return '1fr 1fr'; // Two columns on tablet
    } else {
      return '2fr repeat(3, 1fr)'; // Original layout on desktop
    }
  };

  // Colors based on PlasticAnalytics branding
  const colors = {
    blue: 'rgb(26, 42, 108)',
    orange: 'rgb(230, 126, 34)',
    darkText: '#111827',
    lightText: '#6B7280',
    border: '#E5E7EB',
    hover: 'rgb(26, 42, 108)',
    hoverBg: '#F3F8FF'
  };

  const styles = {
    footer: {
      backgroundColor: 'white',
      borderTop: `1px solid ${colors.border}`,
      padding: windowWidth < 640 ? '32px 0 20px' : '48px 0 24px',
      marginTop: 'auto'
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 20px'
    },
    content: {
      display: 'grid',
      gridTemplateColumns: getGridTemplateColumns(),
      gap: windowWidth < 640 ? '32px' : windowWidth < 1024 ? '40px' : '48px',
      marginBottom: windowWidth < 640 ? '32px' : '48px'
    },
    brandingContainer: {
      gridColumn: windowWidth < 1024 ? '1 / -1' : 'auto', // Full width on tablet and mobile
      marginBottom: windowWidth < 1024 ? '8px' : '0'
    },
    branding: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      maxWidth: windowWidth < 1024 ? '100%' : '300px'
    },
    logo: {
      fontSize: '24px',
      fontWeight: '600',
      color: colors.blue,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    logoIcon: {
      height: '28px',
      borderRadius: '6px'
    },
    tagline: {
      fontSize: '14px',
      color: colors.lightText,
      lineHeight: '1.5',
      maxWidth: windowWidth < 1024 ? '450px' : '300px'
    },
    socialLinks: {
      display: 'flex',
      gap: '12px',
      marginTop: '8px'
    },
    socialIcon: (isHovered) => ({
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isHovered ? colors.hoverBg : '#F3F4F6',
      color: isHovered ? colors.blue : colors.lightText,
      borderRadius: '8px',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    }),
    columnContainer: {
      display: 'flex',
      flexDirection: windowWidth < 640 ? 'column' : 'row',
      gap: windowWidth < 640 ? '32px' : 'inherit',
      gridColumn: windowWidth < 1024 && windowWidth >= 640 ? '1 / -1' : 'auto'
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      flex: windowWidth < 1024 && windowWidth >= 640 ? '1' : 'auto'
    },
    columnTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: colors.darkText,
      marginBottom: '8px'
    },
    link: (key, isHovered) => ({
      fontSize: '14px',
      color: isHovered ? colors.blue : colors.lightText,
      textDecoration: 'none',
      transition: 'color 0.2s ease',
      cursor: 'pointer',
      padding: '4px 0',
      position: 'relative',
      display: 'inline-block',
    }),
    bottom: {
      borderTop: `1px solid ${colors.border}`,
      paddingTop: windowWidth < 768 ? '20px' : '24px',
      display: 'flex',
      flexDirection: windowWidth < 768 ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: windowWidth < 768 ? 'flex-start' : 'center',
      gap: windowWidth < 768 ? '16px' : '0'
    },
    copyright: {
      fontSize: '14px',
      color: colors.lightText
    },
    bottomLinks: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: windowWidth < 480 ? '12px 24px' : '24px',
      marginTop: windowWidth < 768 ? '8px' : '0'
    },
    partnerLogo: {
      height: '60px',
      maxWidth: '180px',
      objectFit: 'contain'
    }
  };

  // Links data
  const footerLinks = {
    solutions: [
      { label: 'Analysis Platform', url: '/analysis' },
      { label: 'Spectral Database', url: '/database' },
      { label: 'Research Tools', url: '/research' }
    ],
    company: [
      { label: 'About Us', url: '/about' },
      { label: 'Careers', url: '/careers' },
      { label: 'Contact', url: '/contact' }
    ],
    resources: [
      { label: 'Documentation', url: '/documentation' },
      { label: 'API Reference', url: '/api' },
      { label: 'Support', url: '/support' }
    ]
  };

  // Handle hover states for links
  const handleLinkHover = (section, index, isHovered) => {
    setHoveredLinks(prev => ({
      ...prev,
      [`${section}-${index}`]: isHovered
    }));
  };
  
  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isSmallMobile = windowWidth <= 480;

  useEffect(() => {
    // Handle window resize for responsiveness
    const handleResize = () => setWindowWidth(window.innerWidth);
    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    // Set the date we're counting down to - end of April
    const countDownDate = new Date("June 2, 2025 23:59:59").getTime();

    // Update the countdown every 1 second
    const x = setInterval(function() {
      // Get current date and time
      const now = new Date().getTime();
        
      // Find the distance between now and the countdown date
      const distance = countDownDate - now;
        
      // Time calculations for days, hours, minutes and seconds
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        
      // Update the countdown elements if they exist
      if (document.getElementById("days")) {
        document.getElementById("days").innerHTML = days;
        document.getElementById("hours").innerHTML = hours;
        document.getElementById("minutes").innerHTML = minutes;
        document.getElementById("seconds").innerHTML = seconds;
      }
        
      // If the countdown is finished
      if (distance < 0) {
        clearInterval(x);
        const countdownContainer = document.getElementById("countdown-container");
        if (countdownContainer) {
          countdownContainer.innerHTML = "<div style='text-align: center; font-size: 1.5rem; color: rgb(17, 29, 74); margin: 2rem 0;'>We're back online!</div>";
        }
      }
    }, 1000);

    // Clean up interval on component unmount
    return () => clearInterval(x);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
  
  
    // Basic email validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    // Call Firebase function using httpsCallable
    const newsletterSignup = httpsCallable(fbFunctions, 'newsletter_signup');
    
    newsletterSignup({
      email: email,
      source: 'maintenance_page'
    })
      .then((result) => {
        if (result.data && result.data.status === 'success') {
          // Clear the form and show success state
          setEmail('');
          
          // Slight delay before showing success message for better animation effect
          setTimeout(() => {
            setSubscribed(true);
          }, 600);
        } else {
          // Handle other status responses
          throw new Error(result.data?.message || 'Failed to subscribe');
        }
      })
      .catch((error) => {
        console.error('Error subscribing to newsletter:', error);
        setError(error.message || 'An error occurred. Please try again.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // Button ripple effect
  const createRipple = (event) => {
    const button = event.currentTarget;
    const ripple = document.createElement('span');
    
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;
    
    const rect = button.getBoundingClientRect();
    
    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${event.clientX - rect.left - radius}px`;
    ripple.style.top = `${event.clientY - rect.top - radius}px`;
    ripple.style.position = 'absolute';
    ripple.style.borderRadius = '50%';
    ripple.style.transform = 'scale(0)';
    ripple.style.animation = 'ripple 0.8s linear';
    ripple.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
    
    button.appendChild(ripple);
    
    setTimeout(() => {
      ripple.remove();
    }, 800);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#fff',
      fontFamily: 'Arial, sans-serif',
      padding: isSmallMobile ? '0.5rem' : isMobile ? '1rem' : '1rem'
    }}>
      
      <style>
        {`
          @keyframes ripple {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }
          @keyframes successAppear {
            0% {
              transform: scale(0.95);
              opacity: 0;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          /* Make elements responsive */
          @media (max-width: 768px) {
            #countdown-container {
              flex-wrap: wrap;
              gap: 1rem !important;
            }
            
            .countdown-box {
              width: calc(50% - 0.5rem) !important;
              min-width: 70px !important;
            }
            
            .partner-logos {
              gap: 1.5rem !important;
            }
          }
          
          @media (max-width: 480px) {
            .countdown-box {
              width: calc(50% - 0.5rem) !important;
              min-width: 60px !important;
            }
            
            .countdown-value {
              font-size: 2rem !important;
            }
            
            .partner-logos {
              flex-direction: column !important;
              gap: 1.5rem !important;
            }
          }
        `}
      </style>

      <div style={{
  maxWidth: '800px',
  width: '100%',
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  padding: isSmallMobile ? '1.5rem' : isMobile ? '2rem' : '2.5rem',
  margin: '2rem 0',
  textAlign: 'center',
  position: 'relative' // Add this to make child absolute positioning relative to this div
}}>
  <div style={{
    position: 'absolute',
    top: isSmallMobile ? '1rem' : '1.5rem',
    left: isSmallMobile ? '1rem' : '1.5rem',
    zIndex: 10
  }}>
    <Button 
      type="secondary" 
      onClick={() => window.location.href = '/'}
      className="return-home-button"
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem' 
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Return Home
      </div>
    </Button>
  </div>
        {/* Logo */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '2.5rem'
        }}>
          <img src="https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Flogos%2Flogo.png?alt=media&token=e2c15ffa-4af0-4f55-af64-545ae75fe511" alt="Plastic Analytics Logo" style={{
            height: '50px'
          }}/>
          <span style={{
            fontWeight: 'bold',
            fontSize: isSmallMobile ? '1.2rem' : '1.5rem',
            color: 'rgb(17, 29, 74)',
            marginLeft: '0.8rem'
          }}>Plastic Analytics™</span>
        </div>
        
        {/* Maintenance message */}
        <h1 style={{
          fontSize: isSmallMobile ? '1.8rem' : isMobile ? '2.2rem' : '2.5rem',
          color: 'rgb(17, 29, 74)',
          margin: '0 0 1.5rem 0',
          fontWeight: '600'
        }}>
          Under Maintenance
        </h1>
        
        <div style={{
          width: '60px',
          height: '4px',
          backgroundColor: 'rgb(17, 29, 74)',
          margin: '0 auto 2.5rem auto'
        }}></div>
        
        <p style={{
          fontSize: isSmallMobile ? '1rem' : '1.1rem',
          lineHeight: '1.6',
          color: '#555',
          marginBottom: '3rem',
          maxWidth: '700px',
          margin: '0 auto 3rem auto'
        }}>
          We're currently upgrading our Spectral Library to serve you better.
          Our platform will be back with improved AI-powered spectroscopic analysis 
          capabilities at the end of April.
        </p>
        
        {/* Countdown section */}
        <h2 style={{
          fontSize: isSmallMobile ? '1.2rem' : '1.4rem',
          color: '#555',
          marginBottom: '2rem',
          fontWeight: '500'
        }}>
          We'll be back in:
        </h2>
        
        <div id="countdown-container" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: isSmallMobile ? '1rem' : '1.5rem',
          margin: '0 0 3rem 0',
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          {/* Days */}
          <div className="countdown-box" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: isMobile ? 'calc(50% - 0.5rem)' : '90px'
          }}>
            <div className="countdown-value" style={{
              backgroundColor: 'rgb(17, 29, 74)',
              color: 'white',
              width: '100%',
              height: isSmallMobile ? '70px' : isMobile ? '80px' : '90px',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: isSmallMobile ? '2rem' : isMobile ? '2.4rem' : '2.8rem',
              fontWeight: 'bold'
            }}>
              <span id="days">14</span>
            </div>
            <span style={{
              marginTop: '0.8rem',
              fontSize: isSmallMobile ? '0.9rem' : '1rem',
              color: '#666',
              fontWeight: '400'
            }}>Days</span>
          </div>
          
          {/* Hours */}
          <div className="countdown-box" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: isMobile ? 'calc(50% - 0.5rem)' : '90px'
          }}>
            <div className="countdown-value" style={{
              backgroundColor: 'rgb(17, 29, 74)',
              color: 'white',
              width: '100%',
              height: isSmallMobile ? '70px' : isMobile ? '80px' : '90px',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: isSmallMobile ? '2rem' : isMobile ? '2.4rem' : '2.8rem',
              fontWeight: 'bold'
            }}>
              <span id="hours">1</span>
            </div>
            <span style={{
              marginTop: '0.8rem',
              fontSize: isSmallMobile ? '0.9rem' : '1rem',
              color: '#666',
              fontWeight: '400'
            }}>Hours</span>
          </div>
          
          {/* Minutes */}
          <div className="countdown-box" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: isMobile ? 'calc(50% - 0.5rem)' : '90px'
          }}>
            <div className="countdown-value" style={{
              backgroundColor: 'rgb(17, 29, 74)',
              color: 'white',
              width: '100%',
              height: isSmallMobile ? '70px' : isMobile ? '80px' : '90px',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: isSmallMobile ? '2rem' : isMobile ? '2.4rem' : '2.8rem',
              fontWeight: 'bold'
            }}>
              <span id="minutes">18</span>
            </div>
            <span style={{
              marginTop: '0.8rem',
              fontSize: isSmallMobile ? '0.9rem' : '1rem',
              color: '#666',
              fontWeight: '400'
            }}>Minutes</span>
          </div>
          
          {/* Seconds */}
          <div className="countdown-box" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: isMobile ? 'calc(50% - 0.5rem)' : '90px'
          }}>
            <div className="countdown-value" style={{
              backgroundColor: 'rgb(17, 29, 74)',
              color: 'white',
              width: '100%',
              height: isSmallMobile ? '70px' : isMobile ? '80px' : '90px',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: isSmallMobile ? '2rem' : isMobile ? '2.4rem' : '2.8rem',
              fontWeight: 'bold'
            }}>
              <span id="seconds">9</span>
            </div>
            <span style={{
              marginTop: '0.8rem',
              fontSize: isSmallMobile ? '0.9rem' : '1rem',
              color: '#666',
              fontWeight: '400'
            }}>Seconds</span>
          </div>
        </div>
        
        {/* Newsletter component */}
        <div style={{
          marginTop: '10px',
          padding: '30px',
          backgroundColor: '#111d4a',
          borderRadius: '8px',
          marginBottom: '2rem',
          transition: 'all 0.8s ease-out',
          textAlign: 'left'
        }}>
          <h2 style={{
            fontSize: isSmallMobile ? '1.6rem' : '2rem',
            fontWeight: '600',
            marginBottom: '24px',
            color: '#ffffff',
            letterSpacing: '-0.025em',
          }}>
            Stay Updated
          </h2>
          
          {subscribed ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '20px 24px',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              animation: 'successAppear 0.8s forwards',
            }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '16px',
                flexShrink: 0,
              }}>
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p style={{
                fontSize: '1.05rem',
                margin: 0,
                color: '#ffffff',
                lineHeight: '1.6',
              }}>
                You're subscribed to our newsletter. We'll notify you when we're back online.
              </p>
            </div>
          ) : (
            <>
              <p style={{
                fontSize: isSmallMobile ? '1rem' : '1.125rem',
                lineHeight: '1.7',
                marginBottom: '36px',
                color: '#e2e8f0',
              }}>
                Get notified when our platform is back online and stay updated on our latest spectroscopic analysis features.
              </p>
              
              <form onSubmit={handleSubmit} style={{
                display: 'flex',
                flexDirection: isSmallMobile ? 'column' : 'row',
                gap: '16px',
                alignItems: isSmallMobile ? 'stretch' : 'flex-start',
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 300px',
                }}>
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      padding: '16px 20px',
                      fontSize: '1.05rem',
                      lineHeight: '1.5',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05) inset',
                      transition: 'all 0.4s ease',
                    }}
                  />
                  {error && (
                    <p style={{
                      color: '#f87171',
                      fontSize: '0.875rem',
                      marginTop: '8px',
                      marginLeft: '4px',
                    }}>
                      {error}
                    </p>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px 28px',
                      backgroundColor: isButtonHovered ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      lineHeight: '1.5',
                      borderRadius: '2rem',
                      border: isButtonHovered ? '1px solid #ffffff' : '1px solid rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      position: 'relative',
                      zIndex: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      boxShadow: isButtonHovered ? '0 6px 16px rgba(0, 0, 0, 0.2)' : '0 4px 8px rgba(0, 0, 0, 0.1)',
                      transform: isButtonHovered ? 'translateY(-2px)' : isButtonActive ? 'translateY(1px)' : 'translateY(0)',
                      opacity: isSubmitting ? 0.7 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    }}
                    onMouseDown={(e) => {
                      setIsButtonActive(true);
                      createRipple(e);
                    }}
                    onMouseUp={() => setIsButtonActive(false)}
                    onMouseEnter={() => setIsButtonHovered(true)}
                    onMouseLeave={() => {
                      setIsButtonHovered(false);
                      setIsButtonActive(false);
                    }}
                  >
                      {isSubmitting ? 'Submitting...' : 'Notify Me'}
                  </button>
                </div>
              </form>
              
              <p style={{
                fontSize: '0.9rem',
                color: 'rgba(255, 255, 255, 0.7)',
                marginTop: '24px',
              }}>
                By subscribing, you'll receive updates about our platform and microplastics research.
              </p>
            </>
          )}
        </div>
        
        {/* Contact information */}
        <p style={{
          fontSize: isSmallMobile ? '0.95rem' : '1rem',
          lineHeight: '1.5',
          color: '#666',
          marginBottom: '1.5rem'
        }}>
          For urgent inquiries, please contact us at:
          <br/>
          <a href="mailto:joseph.levermore@eden-microfluidics.com" style={{
            color: 'rgb(17, 29, 74)',
            fontWeight: '500',
            textDecoration: 'none'
          }}>
            joseph.levermore@eden-microfluidics.com
          </a>
        </p>
        
        {/* Social media icons */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          margin: '2rem 0 3rem 0'
        }}>
        <a 
          href="https://twitter.com/plasticanalytic" 
          style={styles.socialIcon(hoveredSocial === 'twitter')}
          onMouseEnter={() => setHoveredSocial('twitter')}
          onMouseLeave={() => setHoveredSocial(null)}
          aria-label="Twitter"
        >
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
          </svg>
        </a>
          <a 
            href="https://www.linkedin.com/company/plasticanalytics" 
            style={styles.socialIcon(hoveredSocial === 'linkedin')}
            onMouseEnter={() => setHoveredSocial('linkedin')}
            onMouseLeave={() => setHoveredSocial(null)}
            aria-label="LinkedIn"
          >
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
              <circle cx="4" cy="4" r="2"/>
            </svg>
          </a>
        </div>
        
        {/* Presented by section */}
        <div style={{
          marginTop: '2rem',
          borderTop: '1px solid #eee',
          paddingTop: '2rem'
        }}>
          <p style={{
            fontSize: '1rem',
            color: '#666',
            marginBottom: '1.5rem',
            fontWeight: '500'
          }}>
            Presented by:
          </p>
          
          <div className="partner-logos" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isSmallMobile ? '2rem' : isMobile ? '2.5rem' : '3rem',
            flexDirection: 'row',
            flexWrap: 'wrap'
          }}>
            {/* Imperial College London */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60px',
              maxWidth: '180px'
            }}>
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Flogos%2Fimperial-college-london-logo.png?alt=media&token=a2e5bf66-50d7-4be9-ade4-8c53538439d4" 
                alt="Imperial College London" 
                style={styles.partnerLogo}
              />
            </div>
            
            {/* MRC Centre */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60px',
              maxWidth: '180px'
            }}>
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Flogos%2Fmrc-centre-logo.png?alt=media&token=5b047c0f-3a34-4add-9547-fecad60e109f" 
                alt="MRC Centre" 
                style={styles.partnerLogo}
              />
            </div>
            
            {/* Medical Research Council */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60px',
              maxWidth: '180px'
            }}>
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Flogos%2Fmedical-research-council-logo.png?alt=media&token=ca91e8d4-052f-453f-9752-56369a416265" 
                alt="Medical Research Council" 
                style={styles.partnerLogo}
              />
            </div>
          </div>
        </div>
      </div>
      
      <div style={{
        marginTop: '2rem',
        marginBottom: '2rem',
        fontSize: '0.9rem',
        color: '#777',
        textAlign: 'center'
      }}>
        © 2025 Plastic Analytics™. All rights reserved.
      </div>
    </div>
  );
}

export default MaintenanceCountdown;